import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import {
  Entry,
  Flow,
  CustomCollectionsStepAssets,
  StepType,
} from "@dreambigger/shared/src/types";
import prefixedResponses from "@dreambigger/shared/src/utils/prefixedResponses";
import { Conditional } from "@dreambigger/conditional";
import { useApplication } from "../../../api";
import { changeStepForward } from "../../../utils/changeStepForward";
import useNextStep from "../../../hooks/use-next-step";
import extractCustomFields from "../../../utils/extractCustomFields";
import resolveCustomStepTitles, {
  ResolvedCustomStepTitles,
} from "../../../utils/resolveCustomStepTitles";
import compileTemplate from "../../../utils/compileTemplate";
import entriesFromResponseFields from "../../../utils/entriesFromResponseFields";
import runStepHooks from "../utils/runStepHooks";
import shouldDisplayField from "../utils/shouldDisplayField";
import validateAllEntriesCrossRules from "../utils/validateAllEntriesCrossRules";
import entriesToResponseFields from "../utils/entriesToResponseFields";
import loadAutoPopulateData from "../utils/loadAutoPopulateData";
import { CrossEntryValidation } from "../types";

interface UseStepLogicOptions {
  flow: Flow;
  slug: string;
  type: string;
  assets: CustomCollectionsStepAssets;
  /** Screenshot callback from `useCaptureScreenshot`; invoked by handleNext
   * when `assets.captureScreenshot` is enabled. */
  captureScreenshot?: (applicationId: string, slug: string) => Promise<void>;
}

export default function useStepLogic({
  flow,
  slug,
  type,
  assets,
  captureScreenshot,
}: UseStepLogicOptions) {
  const applicationHelper = useApplication(flow.financialInstitution.id);
  const nextStep = useNextStep(assets, applicationHelper, flow, slug);
  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper, flow]
  );

  const [entries, setEntries] = useState<Entry[]>([]);
  const [saving, setSaving] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [expandedEntryId, setExpandedEntryId] = useState<number | null>(null);
  const nextIdRef = useRef(0);
  // Gate the one-shot initialization effect on a ref rather than the
  // `initialized` state so the effect's dep array can legitimately list only
  // `application`. The `initialized` state is still used to gate render.
  const hasInitializedRef = useRef(false);
  // Tracks the latest entries so save/remove can compute the next state from
  // the freshest snapshot — avoids stale-closure overwrites when two saves
  // fire in quick succession before React has re-rendered with the first.
  const entriesRef = useRef<Entry[]>(entries);
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  // Reset the one-shot init gate if the hook instance is reused across a slug
  // change — routing currently prevents this, but the ref-gated init would
  // otherwise silently skip re-initialization and keep the previous slug's
  // entries in memory.
  useEffect(() => {
    hasInitializedRef.current = false;
  }, [slug]);

  const getNextId = useCallback(() => {
    const id = nextIdRef.current;
    nextIdRef.current += 1;
    return id;
  }, []);

  const dc: ResolvedCustomStepTitles = useMemo(
    () => resolveCustomStepTitles(assets),
    [assets]
  );

  const fieldConfigs = useMemo(
    () => extractCustomFields(assets.customFields),
    [assets.customFields]
  );

  // Build effective cross-entry validations by lifting per-field rules off
  // each customField and stamping in the owning field's key, label, and config.
  // fieldConfig is carried so the validator can skip rules on entries where
  // the parent field is hidden by display conditions.
  const effectiveValidations: CrossEntryValidation[] = useMemo(
    () =>
      fieldConfigs.flatMap((f) => {
        const raw = f.fieldCrossEntryValidations;
        const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
        return list.map((v) => ({
          ...v,
          fieldKey: f.fieldKey,
          fieldLabel: f.fieldLabel,
          fieldConfig: f,
        }));
      }),
    [fieldConfigs]
  );

  const hasAutoPopulate = !!assets.autoPopulate?.length;

  // Initialize on application load — runs exactly once when the application
  // becomes available. Ref-gated so the effect dep array is just `application`;
  // `slug`/`assets.autoPopulate`/`fieldConfigs`/`hasAutoPopulate`/`getNextId`
  // intentionally aren't deps — re-running init when those change would
  // overwrite the user's in-progress edits.
  useEffect(() => {
    if (!application || hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    // Determine which autoPopulate configs pass their conditions NOW.
    // Evaluated against the flattened prefixed-responses map so configs can
    // reference prior-step fields via `slug__fieldKey` keys.
    const flattened = prefixedResponses(application.responses);
    const activeIndices = new Set<number>();
    (assets.autoPopulate || []).forEach((config, idx) => {
      if (
        Conditional.evaluate({ conditions: config.conditions, data: flattened })
      ) {
        activeIndices.add(idx);
      }
    });

    const existingResponse = application.responses.find((r) => r.slug === slug);
    if (existingResponse) {
      const existingEntries = entriesFromResponseFields(
        existingResponse.fields
      );

      // Detect stale auto-populated entries: compare which config indices
      // SHOULD be active now vs which _autoPopIndex values are saved.
      const savedAutoPopIndices = new Set(
        existingEntries
          .filter((e) => e._autoPopIndex !== undefined)
          .map((e) => e._autoPopIndex as number)
      );
      const conditionsChanged =
        hasAutoPopulate &&
        (activeIndices.size !== savedAutoPopIndices.size ||
          Array.from(activeIndices).some((i) => !savedAutoPopIndices.has(i)));

      if (!conditionsChanged) {
        setEntries(existingEntries);
        nextIdRef.current =
          Math.max(...existingEntries.map((e) => e.id || 0), 0) + 1;
        setInitialized(true);
        return;
      }

      const manualEntries = existingEntries.filter(
        (e) => e._autoPopIndex === undefined
      );
      const autoEntries: Entry[] = [];
      (assets.autoPopulate || []).forEach((config, idx) => {
        if (!activeIndices.has(idx)) return;
        const data = loadAutoPopulateData(config, flattened, fieldConfigs);
        autoEntries.push({
          id: getNextId(),
          _autoPopIndex: idx,
          ...data,
        });
      });
      const merged = [...autoEntries, ...manualEntries];
      nextIdRef.current = Math.max(...merged.map((e) => e.id || 0), 0) + 1;
      setEntries(merged);
      setInitialized(true);
      return;
    }

    if (hasAutoPopulate) {
      const autoEntries: Entry[] = [];
      (assets.autoPopulate || []).forEach((config, idx) => {
        if (!activeIndices.has(idx)) return;
        const data = loadAutoPopulateData(config, flattened, fieldConfigs);
        autoEntries.push({
          id: getNextId(),
          _autoPopIndex: idx,
          ...data,
        });
      });
      setEntries(autoEntries);
    }

    setInitialized(true);
  }, [application]);

  const saveEntry = useCallback(
    async (entryId: number, data: Partial<Entry>) => {
      if (!application) return;

      // Snapshot from the ref (freshest) so back-to-back saves don't overwrite
      // each other's in-flight updates.
      const snapshot = entriesRef.current;
      const updated = snapshot.map((e) =>
        e.id === entryId ? { ...e, ...data } : e
      );
      setEntries(updated);

      try {
        await applicationHelper.update(application.id, {
          slug,
          type: type as StepType,
          fields: entriesToResponseFields(updated),
        });
      } catch (err) {
        // Roll back to the pre-save snapshot so the UI matches the server.
        // Rethrow so the caller (EntryCard.handleFinish) keeps the card
        // expanded for the user to retry.
        setEntries(snapshot);
        message.error("Failed to save. Please try again.");
        throw err;
      }
    },
    [application, applicationHelper, slug, type]
  );

  const addEntry = useCallback(
    (initialData?: Partial<Entry>) => {
      const newId = getNextId();
      const newEntry: Entry = {
        id: newId,
        ...initialData,
      };
      setEntries((prev) => [...prev, newEntry]);
      setExpandedEntryId(newId);
    },
    [getNextId]
  );

  const removeEntry = useCallback(
    async (entryId: number) => {
      if (!application) return;

      const snapshot = entriesRef.current;
      const updated = snapshot.filter((e) => e.id !== entryId);
      setEntries(updated);

      try {
        await applicationHelper.update(application.id, {
          slug,
          type: type as StepType,
          fields: entriesToResponseFields(updated),
        });
      } catch {
        // Revert the deletion on failure so the entry reappears.
        setEntries(snapshot);
        message.error("Failed to save. Please try again.");
      }
    },
    [application, applicationHelper, slug, type]
  );

  const getUniqueConflict = useCallback(
    (entryId: number, fieldKey: string, value: string): string | null => {
      if (!value) return null;
      const conflict = entries.find(
        (e) => e.id !== entryId && String(e[fieldKey] || "") === value
      );
      if (!conflict) return null;
      return compileTemplate(dc.entryHeader.title, conflict) || "another entry";
    },
    [entries, dc.entryHeader.title]
  );

  const getCrossEntryTotal = useCallback(
    (fieldKey: string, excludeEntryId?: number): number => {
      return entries
        .filter((e) => e.id !== excludeEntryId)
        .reduce((sum, e) => sum + (Number(e[fieldKey]) || 0), 0);
    },
    [entries]
  );

  const getCrossEntryCount = useCallback(
    (fieldKey: string, excludeEntryId?: number): number => {
      return entries.filter(
        (e) => e.id !== excludeEntryId && e[fieldKey] === true
      ).length;
    },
    [entries]
  );

  const requiredFieldKeys = useMemo(
    () => fieldConfigs.filter((f) => f.fieldRequired).map((f) => f.fieldKey),
    [fieldConfigs]
  );

  const getValidationError = useCallback((): string | null => {
    for (const entry of entries) {
      for (const fieldKey of requiredFieldKeys) {
        const fieldConfig = fieldConfigs.find((f) => f.fieldKey === fieldKey);
        if (!fieldConfig) continue;

        if (
          !shouldDisplayField(
            fieldConfig,
            entry,
            application || undefined,
            undefined,
            fieldConfigs
          )
        )
          continue;

        if (
          entry[fieldKey] === undefined ||
          entry[fieldKey] === null ||
          entry[fieldKey] === ""
        ) {
          return `Please complete all required fields for ${
            compileTemplate(dc.entryHeader.title, entry) || "each entry"
          }`;
        }
      }
    }

    if (effectiveValidations.length > 0) {
      const crossError = validateAllEntriesCrossRules({
        entries,
        validations: effectiveValidations,
        entryTitleTemplate: dc.entryHeader.title,
        application: application || undefined,
        allFieldConfigs: fieldConfigs,
      });
      if (crossError) return crossError;
    }

    return null;
  }, [
    entries,
    requiredFieldKeys,
    fieldConfigs,
    effectiveValidations,
    dc,
    application,
  ]);

  const validationResult = useMemo(
    () => getValidationError(),
    [getValidationError]
  );
  const validationMessage = validationResult || "";
  const isValid = validationResult === null;

  // Save all and advance. Returns true on success for callers that need to
  // sequence post-save actions (e.g. analytics tracking). Mirrors the
  // step-wrapper `defaultHandleNext` pipeline (screenshot → save → afterHooks
  // → transition rules → navigate) so the collection variant has parity with
  // the default `custom` step renderer.
  const handleNext = useCallback(async (): Promise<boolean> => {
    if (getValidationError()) return false;

    if (!application) {
      message.error("Unable to find application");
      return false;
    }

    if (assets.captureScreenshot && captureScreenshot) {
      await captureScreenshot(application.id, slug);
    }

    const fields = entriesToResponseFields(entries);

    const runAfterHooks = async () => {
      const hooks = assets?.afterHooks?.hooks;
      if (Array.isArray(hooks) && hooks.length) {
        try {
          await runStepHooks({ hooks, applicationHelper, application });
        } catch {
          console.error("Failed to run after hooks.");
        }
      }
    };

    const finalize = async () => {
      if (assets.runTransitionRules) {
        const { data } = await applicationHelper.getNextStep({
          applicationId: application.id,
          stepSlug: slug,
          transformUUID:
            typeof assets.runTransitionRules === "object"
              ? assets.runTransitionRules.transformUUID
              : undefined,
        });
        if (data.nextStep) {
          changeStepForward(data.nextStep);
          return;
        }
      }
      nextStep(fields);
    };

    // Match default custom-step behavior: when the user hasn't filled in any
    // field (e.g. every field is optional and hidden until a trigger is set),
    // the BE rejects an empty fields object — so skip the PATCH and advance.
    if (Object.keys(fields).length === 0) {
      await finalize();
      return true;
    }

    setSaving(true);
    try {
      const saved = await applicationHelper.update(application.id, {
        slug,
        type: type as StepType,
        fields,
      });
      if (!saved) return false;
      await runAfterHooks();
      await finalize();
      return true;
    } catch {
      message.error("Failed to save. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    application,
    applicationHelper,
    slug,
    type,
    assets,
    entries,
    getValidationError,
    captureScreenshot,
    nextStep,
  ]);

  const canAddEntry = useMemo(() => {
    if (assets.maxRepeats && entries.length >= assets.maxRepeats) return false;
    return true;
  }, [assets.maxRepeats, entries]);

  return {
    entries,
    saving,
    initialized,
    isValid,
    validationMessage,
    expandedEntryId,
    setExpandedEntryId,
    addEntry,
    removeEntry,
    saveEntry,
    handleNext,
    canAddEntry,
    getUniqueConflict,
    getCrossEntryTotal,
    getCrossEntryCount,
    application,
    displayConfig: dc,
    effectiveValidations,
  };
}
